/**
 * One dot per experiment, any metric against any other: a score against cost
 * or latency (the usual trade-off), or one evaluator against another (does
 * fixing one hurt the other?). The axes are the user's pick and live in the URL. Every dot
 * is labelled with its experiment's name, so identity is never colour alone;
 * the chips above double as the legend.
 */

import { ChartTooltip } from "@/components/ui/chart-tooltip";
import {
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { COST, LATENCY, scoreMetrics } from "./metrics";
import { AxisSelect, axisFor, axisName, betterHint } from "./scatter-axes";
import { colourOf } from "./selection";
import type { Comparison } from "./types";

interface Dot {
  x: number;
  y: number;
  label: string;
  name: string;
}

export function CompareScatter({
  data,
  y,
  x,
  onAxes,
}: {
  data: Comparison;
  y: string;
  x: string;
  onAxes: (patch: { y?: string; x?: string }) => void;
}) {
  const metrics = [...scoreMetrics(data), COST, LATENCY];
  const yMetric = metrics.find((m) => m.key === y) ?? metrics[0]!;
  const xMetric = metrics.find((m) => m.key === x) ?? COST;
  const points = new Map(data.points.map((p) => [p.experiment_id, p]));
  const series = data.experiments.map((e, i) => {
    const p = points.get(e.id);
    const xv = xMetric.get(p);
    const yv = yMetric.get(p);
    const dots: Dot[] =
      xv === null || yv === null
        ? []
        : [{ x: xv, y: yv, name: e.name, label: `${short(e.name)} · ${yMetric.format(yv)}` }];
    return { id: e.id, colour: colourOf(i), dots };
  });
  const plotted = series.filter((s) => s.dots.length).length;
  const dots = series.flatMap((s) => s.dots);
  const xAxis = axisFor(
    xMetric,
    dots.map((d) => d.x),
  );
  const yAxis = axisFor(
    yMetric,
    dots.map((d) => d.y),
  );

  return (
    <section className="box flex flex-col gap-2 px-4 py-3.5" aria-label="Scatter">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          {yMetric.label} against {axisName(xMetric)}
        </h2>
        <div className="flex items-center gap-1.5">
          <span aria-hidden className="text-xs text-muted">
            Y
          </span>
          <AxisSelect
            label="Y axis"
            metrics={metrics}
            value={yMetric.key}
            other={xMetric.key}
            onPick={(v) => onAxes({ y: v })}
          />
          <span aria-hidden className="text-xs text-muted">
            X
          </span>
          <AxisSelect
            label="X axis"
            metrics={metrics}
            value={xMetric.key}
            other={yMetric.key}
            onPick={(v) => onAxes({ x: v })}
          />
        </div>
      </div>
      {plotted === 0 ? (
        <p className="py-6 text-center text-[13px] text-muted">No experiment has both values yet.</p>
      ) : (
        <div
          role="img"
          aria-label={`${yMetric.label} against ${axisName(xMetric)} for ${plotted} experiments`}
          className="h-[260px]"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 24, bottom: 4, left: -8 }}>
              <CartesianGrid stroke="var(--color-line-soft)" />
              <XAxis
                type="number"
                dataKey="x"
                domain={xAxis.domain}
                {...(xAxis.ticks ? { ticks: xAxis.ticks, interval: 0 } : {})}
                tickFormatter={xAxis.format}
                tick={{ fontSize: 11, fill: "var(--color-faint)" }}
                tickLine={false}
                axisLine={false}
                name={xMetric.label}
              />
              <YAxis
                type="number"
                dataKey="y"
                domain={yAxis.domain}
                {...(yAxis.ticks ? { ticks: yAxis.ticks, interval: 0 } : {})}
                tickFormatter={yAxis.format}
                tick={{ fontSize: 11, fill: "var(--color-faint)" }}
                tickLine={false}
                axisLine={false}
                name={yMetric.label}
              />
              <ZAxis range={[150, 150]} />
              <ChartTooltip
                cursor={false}
                formatter={(v, name) => (name === xMetric.label ? xMetric : yMetric).format(Number(v))}
                labelFormatter={(_, p) => (p[0]?.payload as Dot | undefined)?.name ?? ""}
              />
              {series.map((s) => (
                <Scatter
                  key={s.id}
                  data={s.dots}
                  fill={s.colour}
                  stroke="var(--color-surface)"
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  <LabelList dataKey="label" content={DotLabel} />
                </Scatter>
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-xs text-muted">{betterHint(yMetric, xMetric)}</p>
    </section>
  );
}

function short(name: string): string {
  return name.length > 22 ? `${name.slice(0, 21)}…` : name;
}

/** One line above the dot (below it when the dot is at the top). Recharts' default label wraps to the dot's width,
 *  which breaks a name into one word per line. */
function DotLabel(props: {
  x?: number | string | undefined;
  y?: number | string | undefined;
  width?: number | string | undefined;
  height?: number | string | undefined;
  value?: unknown;
}) {
  const x = Number(props.x ?? 0) + Number(props.width ?? 0) / 2;
  const top = Number(props.y ?? 0);
  // A dot at the top of the plot has no room above it: label it below instead.
  const y = top < 40 ? top + Number(props.height ?? 0) + 16 : top - 8;
  return (
    <text x={x} y={y} textAnchor="middle" fontSize={12} fill="var(--color-ink)">
      {String(props.value ?? "")}
    </text>
  );
}
